import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { PeriodPicker } from "../components/period-picker";
import { Card, ErrorBox, PageTitle } from "../components/ui";
import { VoidedNotice } from "../components/voided-notice";
import { SendDocumentButton } from "../components/send-document";
import { api } from "../lib/api";
import { periodName } from "../lib/format";
import { parsePeriod, periodParam } from "../lib/session";

const link =
  "inline-flex items-center rounded-md border border-brand-line bg-white px-3 py-1.5 text-sm font-medium hover:bg-brand-tint";

interface LibroIvaDigitalBook {
  count: number;
  excluded: { voucher: string; reason: string }[];
  warnings: { voucher: string; warning: string }[];
}

interface LibroIvaDigitalSummary {
  compras: LibroIvaDigitalBook;
  ventas: LibroIvaDigitalBook;
}

const BOOKS = [
  {
    value: "compras",
    title: "Libro IVA Compras",
    description: "Los comprobantes recibidos del período, uno por fila.",
  },
  {
    value: "ventas",
    title: "Libro IVA Ventas",
    description: "Los comprobantes emitidos del período, uno por fila.",
  },
  {
    value: "ambos",
    title: "Compras y Ventas",
    description:
      "Los dos libros juntos: el Excel trae una hoja por libro y el PDF arranca cada libro en su propia página.",
  },
] as const;

export function BooksPage({
  companyId,
  search,
}: {
  companyId: string;
  search: { periodo?: string };
}) {
  const navigate = useNavigate();
  const { year, month } = parsePeriod(search.periodo);
  const base = `/api/companies/${companyId}/periods/${year}/${month}/books`;
  const arca = useQuery({
    queryKey: ["libro-iva-digital", companyId, year, month],
    queryFn: () => api.get<LibroIvaDigitalSummary>(`${base}/arca`),
    retry: false,
    // Always what the files would have now: vouchers change from many screens (Codex 023).
    staleTime: 0,
    refetchOnMount: "always",
  });
  const anulados = useQuery({
    queryKey: ["books", companyId, year, month, "anulados"],
    queryFn: () => api.get<{ compra: number; venta: number }>(`${base}/anulados`),
    staleTime: 0,
    refetchOnMount: "always",
  });
  return (
    <>
      <PageTitle
        subtitle={`${periodName(year, month)} · con la marca del estudio, listos para entregar o revisar en Excel.`}
        actions={
          <PeriodPicker
            value={periodParam(year, month)}
            onChange={(value) =>
              void navigate({
                to: "/empresas/$companyId/libros",
                params: { companyId },
                search: { periodo: value },
              })
            }
          />
        }
      >
        Libros IVA
      </PageTitle>
      <div className="mb-4 empty:hidden">
        <VoidedNotice
          companyId={companyId}
          periodo={periodParam(year, month)}
          anulados={anulados.data}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {BOOKS.map((book) => (
          <Card key={book.value} title={book.title}>
            <p className="mb-3 text-sm text-brand-muted">{book.description}</p>
            <div className="flex gap-2">
              <a className={link} href={`${base}/${book.value}.pdf`} download>
                Descargar PDF
              </a>
              <a className={link} href={`${base}/${book.value}.xlsx`} download>
                Descargar Excel
              </a>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <SendDocumentButton
                companyId={companyId}
                document={{ kind: "libro_iva_pdf", year, month, book: book.value }}
              />
            </div>
          </Card>
        ))}
      </div>
      <Card title="Libro IVA Digital para ARCA" className="mt-4">
        <p className="mb-3 text-sm text-brand-muted">
          Los archivos TXT para importar en ARCA, con el mismo formato que el sistema anterior: por
          cada libro, uno de comprobantes y uno de alícuotas.
        </p>
        <ErrorBox error={arca.error} />
        {arca.data ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {(["compras", "ventas"] as const).map((book) => {
              const summary = arca.data[book];
              const label = book === "compras" ? "Compras" : "Ventas";
              return (
                <div key={book}>
                  <h3 className="mb-1 text-sm font-semibold">
                    {label} · {summary.count} comprobante{summary.count === 1 ? "" : "s"}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <a className={link} href={`${base}/arca/${book}/comprobantes`} download>
                      TXT {label}
                    </a>
                    <a className={link} href={`${base}/arca/${book}/alicuotas`} download>
                      TXT {label} alícuotas
                    </a>
                  </div>
                  {summary.excluded.length > 0 ? (
                    <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm">
                      <p className="font-medium">Quedan afuera, para cargar a mano en ARCA:</p>
                      <ul className="list-disc pl-5">
                        {summary.excluded.map((e) => (
                          <li key={e.voucher}>
                            {e.voucher}: {e.reason}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {summary.warnings.length > 0 ? (
                    <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm">
                      <p className="font-medium">Van en el archivo, pero revisalas:</p>
                      <ul className="list-disc pl-5">
                        {summary.warnings.map((w) => (
                          <li key={w.voucher}>
                            {w.voucher}: {w.warning}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </Card>
      <p className="mt-4 text-sm text-brand-muted">
        Cada libro trae neto, el IVA de cada alícuota, no gravado y total por comprobante, y al pie
        el IVA por alícuota, las percepciones y los totales. Exento, percepciones y otros tributos
        suman su columna solo si el período los tiene. Notas de crédito en negativo; importes en
        pesos.
      </p>
    </>
  );
}
